import { DOWN_ARROW, END, ENTER, HOME, LEFT_ARROW, PAGE_DOWN, PAGE_UP, RIGHT_ARROW, SPACE, UP_ARROW } from '@angular/cdk/keycodes';
import { AfterContentInit, ChangeDetectorRef, Component, EventEmitter, Input, isDevMode, Optional, Output, ViewChild } from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { CalendarCell, CalendarUserEvent, SimCalendarBodyComponent } from './calendar-body.component';
import { DateRange } from './date-selection-model';
import { createMissingDateImplError } from './datepicker-errors';

/** 一次选择20年 */
export const yearsPerPage = 20;

/** 一行显示4年 */
export const yearsPerRow = 4;

@Component({
  selector: 'sim-multi-year-view',
  templateUrl: './multi-year-view.component.html',
  styleUrls: ['./datepicker.component.scss']
})
export class SimMultiYearViewComponent<D> implements AfterContentInit {
  /** 在这个多年视图中显示的日期(除了年份之外的所有内容都将被忽略) */
  @Input()
  get activeDate(): D {
    return this._activeDate;
  }
  set activeDate(value: D) {
    const oldActiveDate = this._activeDate;
    const validDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value)) || this._dateAdapter.today();
    this._activeDate = this._dateAdapter.clampDate(validDate, this.minDate, this.maxDate);
    if (!isSameMultiYearView(this._dateAdapter, oldActiveDate, this._activeDate, this.minDate, this.maxDate)) {
      this._init();
    }
  }

  /** 当前选定的日期 */
  @Input()
  get selected(): DateRange<D> | D | null {
    return this._selected;
  }
  set selected(value: DateRange<D> | D | null) {
    if (value instanceof DateRange) {
      this._selected = value;
    } else {
      this._selected = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
    }

    this._setSelectedYear(value);
  }
  /** 最小选择日期 */
  @Input()
  get minDate(): D | null {
    return this._minDate;
  }
  set minDate(value: D | null) {
    this._minDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 最大选择日期 */
  @Input()
  get maxDate(): D | null {
    return this._maxDate;
  }
  set maxDate(value: D | null) {
    this._maxDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 用于过滤哪些日期可选的方法 */
  @Input() dateFilter: (date: D) => boolean;

  /**
   * 选择面板 决定 startView 值
   * year 选择多年面板 => multi-year
   * month 选择月份面板 => multi-year | year
   * week 选择周面板 => multi-year | year | month
   * date 选择日期面板 => multi-year | year | month
   * date-time 选择日期时间面板 => multi-year | year | month | date
   */
  @Input() picker: 'year' | 'month' | 'week' | 'date' | 'datetime' = 'date';

  /** 在选定新的一年时发出 */
  @Output() readonly selectedChange: EventEmitter<D> = new EventEmitter<D>();

  /** 发出选定的年份。 这并不意味着所选日期发生了变化 */
  @Output() readonly yearSelected: EventEmitter<D> = new EventEmitter<D>();

  /** 发出选定的年份。 当 picker为 'year' 发出 */
  @Output() readonly userSelection: EventEmitter<CalendarUserEvent<D>> = new EventEmitter();

  /** 在激活任何日期时发出 */
  @Output() readonly activeDateChange: EventEmitter<D> = new EventEmitter<D>();

  @ViewChild(SimCalendarBodyComponent) _calendarBody: SimCalendarBodyComponent<D>;

  /** 年份面板的数据 */
  _years: CalendarCell[][] = [];

  /** 当前的年份 */
  _todayYear: number;

  /** 选定的年份 */
  _selectedYear: number | null;
  private _minDate: D | null;
  private _maxDate: D | null;

  private _selected: DateRange<D> | D | null;

  private _activeDate: D;

  constructor(private _changeDetectorRef: ChangeDetectorRef, @Optional() public _dateAdapter: SimDateAdapter<D>) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('SimDateAdapter');
    }
    this._activeDate = this._dateAdapter.today();
  }

  ngAfterContentInit() {
    this._init();
  }

  _init() {
    this._todayYear = this._dateAdapter.getYear(this._dateAdapter.today());

    const activeYear = this._dateAdapter.getYear(this._activeDate);
    const minYearOfPage = activeYear - getActiveOffset(this._dateAdapter, this.activeDate, this.minDate, this.maxDate);
    console.log(minYearOfPage);
    this._years = [];
    for (let i = 0, row: number[] = []; i < yearsPerPage; i++) {
      row.push(minYearOfPage + i);
      if (row.length === yearsPerRow) {
        this._years.push(row.map(year => this._createCellForYear(year)));
        row = [];
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  _focusActiveCell() {
    this._calendarBody._focusActiveCell();
  }

  _getActiveCell(): number {
    return getActiveOffset(this._dateAdapter, this.activeDate, this.minDate, this.maxDate);
  }

  /** 选择新的一年时处理 */
  _yearSelected(event: CalendarUserEvent<number>) {
    const year = event.value;
    this.yearSelected.emit(this._dateAdapter.createDate(year, 0, 1));

    if (this.picker === 'year') {
      this.userSelection.emit({
        ...event,
        ...{
          value: this._dateAdapter.createDate(
            year,
            this._dateAdapter.getMonth(this.activeDate),
            this._dateAdapter.getDate(this.activeDate),
            this._dateAdapter.getHours(this.activeDate),
            this._dateAdapter.getMinutes(this.activeDate),
            this._dateAdapter.getSeconds(this.activeDate)
          )
        }
      });
      return;
    }

    const month = this._dateAdapter.getMonth(this.activeDate);
    const daysInMonth = this._dateAdapter.getNumDaysInMonth(this._dateAdapter.createDate(year, month, 1));
    this.selectedChange.emit(
      this._dateAdapter.createDate(
        year,
        month,
        Math.min(this._dateAdapter.getDate(this.activeDate), daysInMonth),
        this._dateAdapter.getHours(this.activeDate),
        this._dateAdapter.getMinutes(this.activeDate),
        this._dateAdapter.getSeconds(this.activeDate)
      )
    );
  }

  /** 当日历处于多年视图中时，处理日历主体上的按键事件 */
  _handleCalendarBodyKeydown(event: KeyboardEvent) {
    const oldActiveDate = this._activeDate;
    // tslint:disable-next-line: deprecation
    switch (event.keyCode) {
      case LEFT_ARROW:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, 1);
        break;
      case RIGHT_ARROW:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, -1);
        break;
      case UP_ARROW:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, -yearsPerRow);
        break;
      case DOWN_ARROW:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, yearsPerRow);
        break;
      case HOME:
        this.activeDate = this._dateAdapter.addYears(
          this._activeDate,
          -getActiveOffset(this._dateAdapter, this.activeDate, this.minDate, this.maxDate)
        );
        break;
      case END:
        this.activeDate = this._dateAdapter.addYears(
          this._activeDate,
          yearsPerPage - getActiveOffset(this._dateAdapter, this.activeDate, this.minDate, this.maxDate) - 1
        );
        break;
      case PAGE_UP:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, event.altKey ? -yearsPerPage * 10 : -yearsPerPage);
        break;
      case PAGE_DOWN:
        this.activeDate = this._dateAdapter.addYears(this._activeDate, event.altKey ? yearsPerPage * 10 : yearsPerPage);
        break;
      case ENTER:
      case SPACE:
        this._yearSelected({ value: this._dateAdapter.getYear(this._activeDate), event });
        break;
      default:
        return;
    }
    if (this._dateAdapter.compareDate(oldActiveDate, this.activeDate)) {
      this.activeDateChange.emit(this.activeDate);
    }

    this._focusActiveCell();
    // 防止意外的默认操作，如表单提交
    event.preventDefault();
  }

  /** 创建给定年份的CalendarCell数据 */
  private _createCellForYear(year: number) {
    return new CalendarCell(year, year + '', year + '', this._shouldEnableYear(year));
  }

  /** 给定年份是否启用 */
  private _shouldEnableYear(year: number) {
    // 如果年份大于maxDate，小于minDate，则禁用
    if (
      year === undefined ||
      year === null ||
      (this.maxDate && year > this._dateAdapter.getYear(this.maxDate)) ||
      (this.minDate && year < this._dateAdapter.getYear(this.minDate))
    ) {
      return false;
    }

    // 如果到达这里且没有定义过滤器，则启用
    if (!this.dateFilter) {
      return true;
    }

    const firstOfYear = this._dateAdapter.createDate(year, 0, 1);

    // 如果启用了年份中的任何日期，则将该年份视为已启用
    for (let date = firstOfYear; this._dateAdapter.getYear(date) === year; date = this._dateAdapter.addDays(date, 1)) {
      if (this.dateFilter(date)) {
        return true;
      }
    }

    return false;
  }

  /** 根据模型值设置当前突出显示的年份 */
  private _setSelectedYear(value: DateRange<D> | D | null) {
    this._selectedYear = null;

    if (value instanceof DateRange) {
      const displayValue = value.start || value.end;

      if (displayValue) {
        this._selectedYear = this._dateAdapter.getYear(displayValue);
      }
    } else if (value) {
      this._selectedYear = this._dateAdapter.getYear(value);
    }
  }
}

export function isSameMultiYearView<D>(dateAdapter: SimDateAdapter<D>, date1: D, date2: D, minDate: D | null, maxDate: D | null): boolean {
  const year1 = dateAdapter.getYear(date1);
  const year2 = dateAdapter.getYear(date2);
  const startingYear = getStartingYear(dateAdapter, minDate, maxDate);
  return Math.floor((year1 - startingYear) / yearsPerPage) === Math.floor((year2 - startingYear) / yearsPerPage);
}

/**
 * 首次打开多年视图时，将显示活动的年份。 因此，我们计算了活动年份和 *slot* 之间的多少年，当页面进入视图时，我们的“startingYear”将呈现在其中。
 */
export function getActiveOffset<D>(dateAdapter: SimDateAdapter<D>, activeDate: D, minDate: D | null, maxDate: D | null): number {
  const activeYear = dateAdapter.getYear(activeDate);
  return euclideanModulo(activeYear - getStartingYear(dateAdapter, minDate, maxDate), yearsPerPage);
}

/**
 * 我们选择一个“开始”年，以使最大年在页面的结尾或最小年在页面的开始。
 */
function getStartingYear<D>(dateAdapter: SimDateAdapter<D>, minDate: D | null, maxDate: D | null): number {
  let startingYear = 0;
  if (maxDate) {
    const maxYear = dateAdapter.getYear(maxDate);
    startingYear = maxYear - yearsPerPage + 1;
  } else if (minDate) {
    startingYear = dateAdapter.getYear(minDate);
  }
  return startingYear;
}

/** 获取非负数的余数，即使第一个数是负数 */
function euclideanModulo(a: number, b: number): number {
  return ((a % b) + b) % b;
}
